import { ReactNode } from "react";
import { Control } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { Typography } from "./typography";
import { countries } from "@drpcrd/utils";
import { cn } from "@drpcrd/utils/shared";

type Props = {
  label?: ReactNode;
  description?: ReactNode;
  name: string;
  control: Control<any>;
  placeholder?: string;
  hideLabel?: boolean;
  className?: string;
  disabled?: boolean;
};

/**
 * CountrySelect component for rendering a styled country dropdown with label, description, and error handling.
 */
export const CountrySelect = ({
  control,
  name,
  label = "Country",
  description,
  placeholder = "Select country",
  hideLabel = false,
  className = "",
  disabled = false,
}: Props) => {
  const favoriteCountries = [
    { label: "United Kingdom", value: "GB" },
    { label: "United States", value: "US" },
  ];

  const otherCountries = countries.filter(
    (country) => !favoriteCountries.some((fav) => fav.value === country.value),
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn("space-y-1.5", className)}>
          <div>
            {!hideLabel && (
              <FormLabel className="flex w-full flex-row gap-1">
                <Typography weight="semibold" size="sm">
                  {label}
                </Typography>
              </FormLabel>
            )}

            {description && (
              <FormDescription>
                <Typography color="neutral" size="sm">
                  {description}
                </Typography>
              </FormDescription>
            )}
          </div>

          <Select
            onValueChange={field.onChange}
            defaultValue={field.value}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger className="w-full rounded-lg border border-neutral-500 bg-white px-2 py-[10px] font-sans text-lg font-normal focus-visible:outline-2 focus-visible:outline-black">
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent className="max-h-48">
              {/* Favorite countries */}
              {favoriteCountries.map((country) => (
                <SelectItem key={country.value} value={country.value}>
                  {country.label}
                </SelectItem>
              ))}

              {/* Separator */}
              <div className="my-1 border-t border-neutral-200" />

              {/* Other countries */}
              {otherCountries.map((country) => (
                <SelectItem key={country.value} value={country.value}>
                  {country.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <FormMessage className="font-normal text-pink-500" />
        </FormItem>
      )}
    />
  );
};
